// Préférences personnelles (thème, densité, taille de police, dispositions, filtres favoris).
// Appliquées immédiatement sur <html> ; enregistrées par GET/PUT /preferences quand l'API les
// expose, sinon sur cet appareil seulement, ce qui est dit explicitement à l'utilisateur.
import { PREFERENCES_PAR_DEFAUT, type Disposition, type Preferences } from '@cc/contrats/ui-preferences';
import { lireStockage, ecrireStockage } from '@cc/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { requete } from '../api/client';
import { ErreurApi } from '../api/erreurs';
import { useSession } from '../session/FournisseurSession';

export type ModeEnregistrement = 'serveur' | 'appareil' | 'inconnu';

interface ValeurPreferences {
  preferences: Preferences;
  mode: ModeEnregistrement;
  /** Dernier enregistrement réussi. */
  enregistreLe: Date | null;
  erreur: string | null;
  enCours: boolean;
  modifier: (partiel: Partial<Preferences>) => Promise<boolean>;
  disposition: (tableau: string) => Disposition | null;
  enregistrerDisposition: (tableau: string, d: Disposition | null) => Promise<boolean>;
}

const Contexte = createContext<ValeurPreferences | null>(null);
const CLE_DERNIERES = 'cc-preferences:dernieres';

/** Applique thème, densité et taille sur l'élément racine. */
export function appliquerPreferences(p: Pick<Preferences, 'theme' | 'densite' | 'taille_police'>): void {
  const racine = document.documentElement;
  if (p.theme === 'jour') racine.setAttribute('data-theme', 'light');
  else if (p.theme === 'nuit') racine.setAttribute('data-theme', 'dark');
  else racine.removeAttribute('data-theme');
  racine.setAttribute('data-densite', p.densite);
  racine.setAttribute('data-taille', p.taille_police);
}

/** Dernières préférences connues sur cet appareil, appliquées avant la connexion (pas d'éclair). */
export function appliquerPreferencesInitiales(): void {
  appliquerPreferences({ ...PREFERENCES_PAR_DEFAUT, ...(lireStockage<Preferences>(CLE_DERNIERES) ?? {}) });
}

export function FournisseurPreferences({ children }: { children: ReactNode }) {
  const { etat, contexte } = useSession();
  const client = useQueryClient();
  const compteId = contexte?.compte.id ?? null;
  const cleAppareil = compteId ? `cc-preferences:${compteId}` : null;
  const [enregistreLe, setEnregistreLe] = useState<Date | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [locales, setLocales] = useState<Preferences | null>(null);
  // Choix en cours d'enregistrement, affichés aussitôt : sur une connexion lente, le bouton choisi
  // doit être coché sans attendre la réponse du serveur (R18).
  const [enAttente, setEnAttente] = useState<Partial<Preferences> | null>(null);

  const serveur = useQuery({
    queryKey: ['preferences', compteId],
    queryFn: ({ signal }) => requete<Preferences>('/preferences', { signal }),
    enabled: etat === 'connecte' && compteId !== null,
    retry: false,
    staleTime: Infinity,
  });

  const routeAbsente = serveur.error instanceof ErreurApi && serveur.error.routeIndisponible;
  const mode: ModeEnregistrement = serveur.data ? 'serveur' : routeAbsente ? 'appareil' : 'inconnu';

  useEffect(() => {
    if (cleAppareil) setLocales(lireStockage<Preferences>(cleAppareil));
  }, [cleAppareil]);

  const preferences = useMemo<Preferences>(() => {
    const source = mode === 'serveur' ? serveur.data : mode === 'appareil' ? locales : (locales ?? lireStockage<Preferences>(CLE_DERNIERES));
    return { ...PREFERENCES_PAR_DEFAUT, ...(source ?? {}), ...(enAttente ?? {}) };
  }, [mode, serveur.data, locales, enAttente]);

  useEffect(() => {
    appliquerPreferences(preferences);
    ecrireStockage(CLE_DERNIERES, { theme: preferences.theme, densite: preferences.densite, taille_police: preferences.taille_police });
  }, [preferences]);

  const courantes = useRef(preferences);
  courantes.current = preferences;

  const modifier = useCallback(
    async (partiel: Partial<Preferences>) => {
      const suivantes = { ...courantes.current, ...partiel };
      appliquerPreferences(suivantes);
      setEnAttente((x) => ({ ...x, ...partiel }));
      setErreur(null);
      setEnCours(true);
      try {
        if (mode === 'serveur') {
          const r = await requete<Preferences>('/preferences', { methode: 'PUT', corps: partiel });
          client.setQueryData(['preferences', compteId], r);
        } else {
          if (!cleAppareil || !ecrireStockage(cleAppareil, suivantes)) throw new Error('Le stockage de cet appareil est indisponible');
          setLocales(suivantes);
        }
        setEnregistreLe(new Date());
        return true;
      } catch (e) {
        appliquerPreferences(courantes.current);
        setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
        return false;
      } finally {
        // Seules les valeurs de cet appel quittent l'attente ; un choix plus récent, encore en vol, reste affiché.
        setEnAttente((x) => {
          const reste: Record<string, unknown> = { ...x };
          for (const [cle, valeur] of Object.entries(partiel)) if (reste[cle] === valeur) delete reste[cle];
          return Object.keys(reste).length > 0 ? (reste as Partial<Preferences>) : null;
        });
        setEnCours(false);
      }
    },
    [mode, client, compteId, cleAppareil],
  );

  const disposition = useCallback((tableau: string) => preferences.dispositions[tableau] ?? null, [preferences]);

  const enregistrerDisposition = useCallback(
    (tableau: string, d: Disposition | null) => {
      const dispositions = { ...courantes.current.dispositions };
      if (d) dispositions[tableau] = d;
      else delete dispositions[tableau];
      return modifier({ dispositions });
    },
    [modifier],
  );

  const valeur = useMemo(
    () => ({ preferences, mode, enregistreLe, erreur, enCours, modifier, disposition, enregistrerDisposition }),
    [preferences, mode, enregistreLe, erreur, enCours, modifier, disposition, enregistrerDisposition],
  );
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function usePreferences(): ValeurPreferences {
  const v = useContext(Contexte);
  if (!v) throw new Error('usePreferences hors de FournisseurPreferences');
  return v;
}
